import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Receipt, Plus, Landmark } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useStore } from '../../context/StoreContext';
import { SMIIFY_API } from '../../config/api';
import styles from './ExpenseManagement.module.css';

import ExpenseMetrics from './components/ExpenseMetrics/ExpenseMetrics';
import ExpenseTable from './components/ExpenseTable/ExpenseTable';
import AddExpenseDrawer from './components/AddExpenseDrawer/AddExpenseDrawer';
import GlobalDateFilter from '../../components/GlobalDateFilter/GlobalDateFilter';

const API_BASE = `${SMIIFY_API}/expenses`;

// Indian shop date as YYYY-MM-DD (same day the cash drawer and daily report use)
const getTodayString = () => new Date(Date.now() + 330 * 60000).toISOString().slice(0, 10);
const isCash = (exp) => String(exp.payMode || '').toLowerCase() === 'cash';

const ExpenseManagement = () => {
    const navigate = useNavigate();
    const { hasPermission, constraint, user } = useAuth();
    const { activeStore } = useStore();
    const token = localStorage.getItem('smiify_token');
    const storeId = activeStore?._id || activeStore?.id;
    const isOwner = user?.role === 'SUPERADMIN';

    // Live access (the server checks the same rules)
    const canCreate = hasPermission('expense', 'create');
    const canApprove = hasPermission('expense', 'approve');
    const canExport = hasPermission('expense', 'export');
    const canSeeDrawer = hasPermission('cashflow', 'view');
    const allowHistoryView = isOwner || constraint('expense', 'allowHistoryView', false) === true;
    const allowBackdate = isOwner || constraint('expense', 'allowBackdate', false) === true;

    const todayStr = getTodayString();
    const [expenses, setExpenses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [dateRange, setDateRange] = useState({ startDate: todayStr, endDate: todayStr, label: 'Today' });

    // no history access: always today
    useEffect(() => {
        if (!allowHistoryView && (dateRange.startDate !== todayStr || dateRange.endDate !== todayStr)) {
            setDateRange({ startDate: todayStr, endDate: todayStr, label: 'Today' });
        }
    }, [allowHistoryView, dateRange.startDate, dateRange.endDate, todayStr]);

    useEffect(() => {
        const fetchExpenses = async () => {
            if (!storeId || storeId === 'global_hq') { setIsLoading(false); return; }
            setIsLoading(true);
            setLoadError('');
            try {
                const qs = dateRange.startDate && dateRange.endDate ? `?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}` : '';
                const response = await fetch(`${API_BASE}${qs}`, { headers: { Authorization: `Bearer ${token}`, 'x-store-id': storeId } });
                const result = await response.json().catch(() => ({}));
                if (!response.ok || !result.success) throw new Error(result.message || 'Could not load expenses.');
                setExpenses(Array.isArray(result.data) ? result.data : []);
            } catch (error) {
                setExpenses([]);
                setLoadError(error.message === 'Failed to fetch' ? 'Cannot reach the server.' : error.message);
            } finally {
                setIsLoading(false);
            }
        };
        fetchExpenses();
    }, [storeId, dateRange.startDate, dateRange.endDate, token]);

    const metrics = useMemo(() => {
        let pendingAmt = 0; let approvedAmt = 0; let plannedAmt = 0; let rejectedAmt = 0; let pendingCount = 0;
        let cashPaidAmt = 0; let onlinePaidAmt = 0;
        expenses.forEach((exp) => {
            const amt = Number(exp.amount) || 0;
            if (exp.type === 'Planned') { plannedAmt += amt; return; }       // a budget, no money spent yet
            if (exp.status === 'rejected') { rejectedAmt += amt; return; }
            if (exp.status === 'pending') { pendingAmt += amt; pendingCount += 1; }
            if (exp.status === 'approved') approvedAmt += amt;
            // paid money (approved or still waiting for approval) left the shop today
            if (isCash(exp)) cashPaidAmt += amt; else onlinePaidAmt += amt;
        });
        return { pendingAmt, approvedAmt, plannedAmt, rejectedAmt, pendingCount, cashPaidAmt, onlinePaidAmt, totalAmt: approvedAmt + pendingAmt + plannedAmt };
    }, [expenses]);

    const updateStatus = async (id, status, remark = '') => {
        if (!canApprove) { alert("You don't have access to approve expenses."); return; }
        try {
            const res = await fetch(`${API_BASE}/${id}/status`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'x-store-id': storeId },
                body: JSON.stringify({ status, remark }),
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok || json.success === false) { alert(json.message || 'Could not update the expense.'); return; }
            setExpenses((list) => list.map((e) => (e._id === id ? { ...e, status, remark } : e)));
        } catch (error) {
            alert('Cannot reach the server.');
        }
    };

    const handleApprove = (id) => { if (window.confirm('Approve this expense?')) updateStatus(id, 'approved'); };
    const handleReject = (id) => {
        const reason = window.prompt('Reason for rejection:');
        if (reason !== null) updateStatus(id, 'rejected', reason || 'Declined by Admin');
    };

    const handleAddExpenseSubmit = async (expenseData) => {
        try {
            const res = await fetch(API_BASE, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'x-store-id': storeId },
                body: JSON.stringify({ ...expenseData, isAutoApproved: canApprove }),
            });
            const result = await res.json().catch(() => ({}));
            if (!res.ok || !result.success) { alert(result.message || 'Could not record the expense.'); return false; }
            const d = String(result.data?.date || expenseData.date).slice(0, 10);
            if (d >= dateRange.startDate && d <= dateRange.endDate) setExpenses((list) => [result.data, ...list]);
            setIsAddOpen(false);
            return true;
        } catch (error) {
            alert('Cannot reach the server.');
            return false;
        }
    };

    return (
        <div className={styles.pageContainer}>
            <header className={styles.header}>
                <div className={styles.headerTitle}>
                    <div className={styles.iconBox}><Receipt size={16} strokeWidth={2.5} /></div>
                    <div>
                        <h2>Expense Management</h2>
                        <p>Track what the shop spends. Cash expenses are taken off the cash drawer.</p>
                    </div>
                </div>
                <div className={styles.headerActions}>
                    <div title={!allowHistoryView ? "You can only view today's expenses" : ''}>
                        <GlobalDateFilter
                            startDate={dateRange.startDate}
                            endDate={dateRange.endDate}
                            onChange={(range) => setDateRange(range)}
                            disabled={!allowHistoryView}
                            defaultLabel={dateRange.label}
                        />
                    </div>
                    {canSeeDrawer && (
                        <button type="button" className={styles.secondaryBtn} onClick={() => navigate('/cash-flow')}>
                            <Landmark size={14} strokeWidth={2.5} /> Cash drawer
                        </button>
                    )}
                    {canCreate && (
                        <button type="button" className={styles.primaryBtn} onClick={() => setIsAddOpen(true)}>
                            <Plus size={14} strokeWidth={2.5} /> Record Expense
                        </button>
                    )}
                </div>
            </header>

            <div className={styles.contentWrapper}>
                <ExpenseMetrics metrics={metrics} />
                {isLoading ? (
                    <div className={styles.loadingState}><span className={styles.spinner} /> Loading expense records...</div>
                ) : loadError ? (
                    <div className={`${styles.loadingState} ${styles.errorState}`}>{loadError}</div>
                ) : (
                    <ExpenseTable
                        expenses={expenses}
                        canApprove={canApprove}
                        canExport={canExport}
                        rangeLabel={`${dateRange.startDate}_to_${dateRange.endDate}`}
                        onApprove={handleApprove}
                        onReject={handleReject}
                    />
                )}
            </div>

            <AddExpenseDrawer isOpen={isAddOpen} onClose={() => setIsAddOpen(false)} onSubmit={handleAddExpenseSubmit} allowBackdate={allowBackdate} />
        </div>
    );
};

export default ExpenseManagement;
